<script setup lang="ts">
    import { computed } from 'vue';
    import BoolElement from '@/shared/components/BoolElement.vue';
    import UriElement from '@/shared/components/UriElement.vue';
    import { getCodeLinkByB64Id, getLibraryLink } from '@/shared/helpers/routing';
    import { getImageDataURL } from '@/shared/helpers/svgHelpers';
    import { attachmentValueIdx, type CodeCellRendererParams } from './CodeGrid.helper';

    const maxSvgHeight = 100;
    const maxSvgWidth = 200;

    const props = defineProps<{
        params: CodeCellRendererParams;
    }>();

    const row = computed(() => props.params.data);
    const col = computed(() => props.params.col);
    const item = computed(() => row.value?.codeItems[col.value.valueIdx]);
    const columnType = computed(() => col.value.type.toUpperCase());
    const text = computed(() => props.params.value?.toString() ?? '');
    const attachmentKey = computed(() => row.value?.codeItems[attachmentValueIdx]?.value);
</script>

<template>
    <span class="code-cell">
        <template v-if="col.field === 'name'">
            <router-link
                v-if="row"
                :to="getCodeLinkByB64Id(row.id)">
                {{ text }}
            </router-link>
            <a
                v-if="attachmentKey"
                class="attachment-button"
                @click="params.onDownloadAttachment(attachmentKey)">
                <b-tooltip
                    label="Download attachment"
                    position="is-right"
                    append-to-body>
                    <b-icon
                        icon="paperclip"
                        size="is-small" />
                </b-tooltip>
            </a>
        </template>
        <template v-else-if="columnType === 'BOOL'">
            <bool-element :value="text" />
        </template>
        <template v-else-if="columnType === 'CODEREF'">
            <router-link
                v-if="item && item.value !== '' && item.codeId"
                :to="getCodeLinkByB64Id(item.codeId)">
                {{ text }}
            </router-link>
            <span v-else>{{ text }}</span>
        </template>
        <template v-else-if="columnType === 'LIBRARYREF'">
            <router-link
                v-if="text !== ''"
                :to="getLibraryLink(text)">
                {{ text }}
            </router-link>
        </template>
        <template v-else-if="columnType === 'URI'">
            <uri-element :value="text" />
        </template>
        <template v-else-if="col.field === 'attachmentKey'">
            <a
                v-if="text"
                @click="params.onDownloadAttachment(text)">
                <b-tooltip
                    label="Download attachment"
                    position="is-right"
                    append-to-body>
                    <b-icon
                        icon="paperclip"
                        size="is-small" />
                </b-tooltip>
                {{ text }}
            </a>
        </template>
        <template v-else-if="col.type === 'Svg'">
            <img
                v-if="text"
                :src="getImageDataURL(text, maxSvgHeight, maxSvgWidth)">
        </template>
    </span>
</template>

<style scoped>
/* AG Grid renders cell components detached from the grid template, so a single
   root element is required for the cell content to be rendered in full. */
.code-cell {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
}

.attachment-button {
    float: right;
    color: red;
}
</style>
